
import { useEffect, useRef } from 'react'
import type { Node, Edge } from '@xyflow/react'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'
import { createTextFileNode, createImageFileNode, createVideoFileNode } from '@/lib/canvas'
import type { HandleMeta, GenieCanvasNode } from '@/lib/canvas/types'
import type { CanvasNodeData, HandleUpdateParams } from '@/types/canvas'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'

interface UseCanvasWindowGlobalsParams {
  canvasId: string
  nodes: Node[]
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  saveNodes: (nodes: Node[]) => void
  saveEdges: (edges: Edge[]) => void
  toReactFlowNode: (node: ReturnType<typeof createTextFileNode>) => Node
}

/**
 * Manages all bridge store callbacks for cross-component communication.
 *
 * Responsibilities:
 * - handleUpdateCallback: Handle updates from NodeSettingsPanel
 * - createPlaceholderCallback: Placeholder node creation for generation
 * - updateNodeStateCallback: Node state patching for generation progress
 */
export function useCanvasWindowGlobals({
  canvasId,
  nodes,
  setNodes,
  setEdges,
  saveNodes,
  saveEdges,
  toReactFlowNode,
}: UseCanvasWindowGlobalsParams) {
  // Ref for placeholder callback to avoid re-registering on every nodes change
  const nodesRef = useRef(nodes)
  useEffect(() => { nodesRef.current = nodes }, [nodes])

  // ============================================================================
  // DYNAMIC HANDLE UPDATE SYSTEM (Bridge Store Callback)
  // ============================================================================

  /**
   * Setup bridge store callback for handle updates from NodeSettingsPanel
   *
   * When model changes in settings panel:
   * 1. NodeSettingsPanel calculates handle changes
   * 2. Calls this callback via bridge store handleUpdateCallback
   * 3. We remove edges connected to removed handles (prevent orphaned edges)
   * 4. We update the node's handles array (add, update metadata, remove)
   * 5. React Flow re-renders with new handles
   */
  useEffect(() => {
    const handleUpdateCallback = ({
      nodeId,
      handlesToAdd,
      handlesToUpdate,
      handlesToRemove,
    }: HandleUpdateParams) => {
      // Remove edges connected to removed handles BEFORE removing handles
      if (handlesToRemove.length > 0) {
        setEdges((prevEdges) => {
          const updatedEdges = prevEdges.filter((edge) => {
            const isConnectedToRemovedHandle =
              (edge.source === nodeId && edge.sourceHandle && handlesToRemove.includes(edge.sourceHandle)) ||
              (edge.target === nodeId && edge.targetHandle && handlesToRemove.includes(edge.targetHandle))

            return !isConnectedToRemovedHandle
          })
          saveEdges(updatedEdges)
          return updatedEdges
        })
      }

      setNodes((prevNodes) => {
        const updatedNodes = prevNodes.map((node) => {
          if (node.id !== nodeId) return node

          const nodeData = node.data as unknown as CanvasNodeData
          let updatedHandles = (nodeData?.handles || []).filter(
            (handle: HandleMeta) => !handlesToRemove.includes(handle.id)
          )

          // Update existing handles' metadata (label, description, icon)
          updatedHandles = updatedHandles.map((handle: HandleMeta) => {
            const updateInfo = handlesToUpdate?.find((u) => u.id === handle.id)
            if (updateInfo) {
              return { ...handle, ...updateInfo.updates }
            }
            return handle
          })

          // Add new handles
          updatedHandles = [...updatedHandles, ...handlesToAdd]

          return {
            ...node,
            data: {
              ...node.data,
              handles: updatedHandles,
            },
          } as Node
        })
        saveNodes(updatedNodes)
        return updatedNodes
      })

      // Force React Flow to re-measure handle positions
      requestAnimationFrame(() => {
        const updateFn = useCanvasBridgeStore.getState().updateNodeInternals
        if (updateFn) {
          updateFn(nodeId)
        }
      })
    }

    useCanvasBridgeStore.getState().registerHandleUpdate(handleUpdateCallback)
    return () => {
      useCanvasBridgeStore.getState().registerHandleUpdate(null)
    }
  }, [setNodes, setEdges, saveEdges, saveNodes])

  // ============================================================================
  // PLACEHOLDER NODE CREATION (Bridge Store)
  // ============================================================================

  /**
   * Creates placeholder output nodes immediately when generation starts.
   * Nodes are shown with a loading state; content is filled in later via
   * updateNodeStateCallback once generation completes.
   */
  useEffect(() => {
    const createPlaceholderCallback = ({
      parentNodeId,
      type,
      count,
    }: {
      parentNodeId: string
      type: 'image' | 'text' | 'video'
      count: number
    }): string[] => {
      const parentNode = nodesRef.current.find(n => n.id === parentNodeId)
      if (!parentNode) {
        console.error('[Placeholder] Parent node not found:', parentNodeId)
        return []
      }

      const parentX = parentNode.position.x
      const parentY = parentNode.position.y
      const parentWidth = parentNode.measured?.width
        ?? parentNode.width
        ?? (typeof parentNode.style?.width === 'number' ? parentNode.style.width : undefined)
        ?? NODE_DIMENSIONS[parentNode.type || '']?.width
        ?? 500
      const horizontalGap = 100
      const verticalGap = 20
      const nodeHeight = type === 'image'
        ? (NODE_DIMENSIONS.ImageFile?.height || 500)
        : type === 'video'
        ? (NODE_DIMENSIONS.VideoFile?.height || 500)
        : (NODE_DIMENSIONS.TextFile?.height || 500)

      const startX = parentX + parentWidth + horizontalGap
      const startY = parentY

      const newNodes: GenieCanvasNode[] = []
      const newEdges: Edge[] = []
      const newNodeIds: string[] = []

      for (let i = 0; i < count; i++) {
        const position = { x: startX, y: startY + i * (nodeHeight + verticalGap) }
        const newNode = type === 'image'
          ? createImageFileNode(position, undefined, parentNodeId)
          : type === 'video'
          ? createVideoFileNode(position, undefined, parentNodeId)
          : createTextFileNode(position, undefined, parentNodeId)

        if (newNode.component?.state) {
          newNode.component.state.canvasId = canvasId
          newNode.component.state.generationStatus = 'loading'
          newNode.component.state.generationProgress = 0
        }

        newNodes.push(newNode)
        newNodeIds.push(newNode.id)

        // Edge: parent generated-output → child parent-input
        const parentHandles = (parentNode.data as unknown as CanvasNodeData)?.handles
        const childHandles = newNode.handles
        const hasSourceHandle = parentHandles?.some((h: HandleMeta) => h.id === 'generated-output')
        const hasTargetHandle = childHandles?.some((h: HandleMeta) => h.id === 'parent-input')

        if (hasSourceHandle && hasTargetHandle) {
          newEdges.push({
            id: `edge-${parentNodeId}-${newNode.id}`,
            type: 'bezier',
            source: parentNodeId,
            target: newNode.id,
            sourceHandle: 'generated-output',
            targetHandle: 'parent-input',
          })
        }
      }

      // Add nodes and edges together — React Flow v12 handles rendering
      // edges correctly once handle bounds are measured by ResizeObserver
      const reactFlowNodes = newNodes.map(toReactFlowNode)
      setNodes(prevNodes => {
        const existingIds = new Set(prevNodes.map(n => n.id))
        const nodesToAdd = reactFlowNodes.filter(n => !existingIds.has(n.id))
        if (nodesToAdd.length === 0) return prevNodes
        const updated = [...prevNodes, ...nodesToAdd]
        saveNodes(updated)
        return updated
      })

      if (newEdges.length > 0) {
        setEdges(prevEdges => {
          const existingIds = new Set(prevEdges.map(e => e.id))
          const edgesToAdd = newEdges.filter(e => !existingIds.has(e.id))
          if (edgesToAdd.length === 0) return prevEdges
          const updated = [...prevEdges, ...edgesToAdd]
          saveEdges(updated)
          return updated
        })
      }

      // After nodes render and ResizeObserver measures handles,
      // force re-measurement and fit view. updateNodeInternals
      // internally uses RAF, so fitView needs an additional frame.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const store = useCanvasBridgeStore.getState()
          const updateFn = store.updateNodeInternals
          if (updateFn) {
            updateFn(parentNodeId)
            newNodeIds.forEach(id => updateFn(id))
          }
          // Wait one more frame for handle bounds to be applied
          requestAnimationFrame(() => {
            store.fitView?.([parentNodeId, ...newNodeIds])
          })
        })
      })

      return newNodeIds
    }

    useCanvasBridgeStore.getState().registerCreatePlaceholder(createPlaceholderCallback)
    return () => {
      useCanvasBridgeStore.getState().registerCreatePlaceholder(null)
    }
  }, [canvasId, setNodes, setEdges, saveNodes, saveEdges, toReactFlowNode])

  // Register saveNodes in bridge store so custom nodes can trigger saves
  useEffect(() => {
    useCanvasBridgeStore.getState().registerSaveNodes(saveNodes as (nodes: unknown[]) => void)
    return () => {
      useCanvasBridgeStore.getState().registerSaveNodes(null)
    }
  }, [saveNodes])

  // Register addCanvasNodes/addCanvasEdges — uses CanvasPage's setNodes/setEdges
  // Note: does NOT auto-save on each add to avoid rapid concurrent saves during batch.
  // Caller is responsible for triggering save when appropriate (e.g., after batch completes).
  useEffect(() => {
    let saveTimer: ReturnType<typeof setTimeout> | null = null

    const addCanvasNodes = (newNodes: unknown[]) => {
      setNodes((current) => [...current, ...(newNodes as Node[])])
      // Debounced save — coalesces rapid additions into a single save
      if (saveTimer) clearTimeout(saveTimer)
      saveTimer = setTimeout(() => {
        setNodes((current) => {
          saveNodes(current)
          return current
        })
      }, 2000)
    }
    const addCanvasEdges = (newEdges: unknown[]) => {
      setEdges((current) => {
        const updated = [...current, ...(newEdges as Edge[])]
        saveEdges(updated)
        return updated
      })
    }
    useCanvasBridgeStore.getState().registerAddCanvasNodes(addCanvasNodes)
    useCanvasBridgeStore.getState().registerAddCanvasEdges(addCanvasEdges)
    return () => {
      if (saveTimer) clearTimeout(saveTimer)
      useCanvasBridgeStore.getState().registerAddCanvasNodes(null)
      useCanvasBridgeStore.getState().registerAddCanvasEdges(null)
    }
  }, [setNodes, setEdges, saveNodes, saveEdges])

  // ============================================================================
  // NODE STATE PATCH (Bridge Store)
  // ============================================================================

  /**
   * Patches a single node's state. Used to push generation progress and final
   * content into placeholder nodes created by createPlaceholderCallback.
   */
  useEffect(() => {
    const updateNodeStateCallback = (nodeId: string, stateUpdate: Record<string, unknown>) => {
      setNodes(prevNodes => {
        const updated = prevNodes.map(n => {
          if (n.id !== nodeId) return n
          const currentState = (n.data as Record<string, unknown>)?.state as Record<string, unknown> || {}
          return {
            ...n,
            data: {
              ...n.data,
              state: { ...currentState, ...stateUpdate },
            },
          } as Node
        })
        saveNodes(updated)
        return updated
      })
    }

    useCanvasBridgeStore.getState().registerUpdateNodeState(updateNodeStateCallback)
    return () => {
      useCanvasBridgeStore.getState().registerUpdateNodeState(null)
    }
  }, [setNodes, saveNodes])

  // Register swap node type callback (TextNode ↔ TextFile etc.)
  useEffect(() => {
    const COMPONENT_TITLES: Record<string, string> = {
      TextNode: 'Text Node',
      TextFile: 'Text File',
    }

    const swapNodeTypeCallback = (nodeId: string, newComponentType: string) => {
      const newTitle = COMPONENT_TITLES[newComponentType]
      setNodes((prev) =>
        prev.map((node) => {
          if (node.id !== nodeId) return node
          const data = node.data as Record<string, unknown>
          const state = (data?.state as Record<string, unknown>) || {}
          return {
            ...node,
            type: newComponentType,
            data: {
              ...data,
              title: state.titleSource === 'automatic' ? newTitle || state.title : state.title,
              state: {
                ...state,
                title: state.titleSource === 'automatic' ? newTitle || state.title : state.title,
              },
            },
          }
        })
      )
    }

    useCanvasBridgeStore.getState().registerSwapNodeType(swapNodeTypeCallback)
    return () => {
      useCanvasBridgeStore.getState().registerSwapNodeType(null)
    }
  }, [setNodes])

}
